import styled from "@emotion/styled"
import { motion, useInView } from "framer-motion"
import { useId, useRef } from "react"

import { slideUp } from "animations/slideUp"

import { useConsole } from "contexts/Console"

import getMediaQuery from "css/breakpoints"
import { Heading as HeadingCN } from "css/classnames"
import { Chapo as ChapoClass } from "css/classnames"
import { Paragraph as ParagraphClass } from "css/classnames"
import { body100, headline50, headline100, light, surtitle50, surtitle100 } from "css/text"

import Link from "components/link/Link"
import { textWidths } from "components/push/commons"
import Chapo from "components/text/Chapo"
import Heading, { KickerCN, SubtitleCN } from "components/text/Heading"
import TextBlock from "components/text/TextBlock"

const Root = styled(motion.div)`
  /* grid-column: 2 / span 5; */
  grid-row: 1 / -1;
  z-index: ${({ pushcenter }) => (pushcenter ? "1" : "unset")};

  /* margin-block-end: 16vw; */
  margin-block: ${({ pushcenter }) => (pushcenter ? "auto" : "0 16vw")};
  display: ${({ pushcenter }) => pushcenter && "flex"};
  flex-direction: ${({ pushcenter }) => pushcenter && "column"};
  align-items: ${({ pushcenter }) => pushcenter && "center"};
  margin-block: ${({ pushcenter }) => pushcenter && "auto"};
  grid-column: ${({ pushcenter }) => (pushcenter ? "2 / span 6" : "2 / span 5")};

  html.prefers-contrast & {
    > a,
    h2 {
      position: relative;
      z-index: 1;
    }

    > hgroup {
      position: relative;
      z-index: 1;
    }
  }

  ${getMediaQuery("m")} {
    /* margin-block-end: 5vw; */
    margin-block: ${({ pushcenter }) => (pushcenter ? "auto" : "0 5vw")};
    /* grid-column: 2 / span 6; */
    grid-column: ${({ pushcenter }) => (pushcenter ? "5 / span 6" : "2 / span 6")};

    &.${textWidths.small} {
      /* grid-column: 2 / span 5; */
      grid-column: ${({ pushcenter }) => (pushcenter ? "5 / span 5" : "2 / span 5")};
    }
    &.${textWidths.medium} {
      /* grid-column: 2 / span 6; */
      grid-column: ${({ pushcenter }) => (pushcenter ? "5 / span 6" : "2 / span 6")};
    }
    &.${textWidths.large} {
      /* grid-column: 2 / span 8; */
      grid-column: ${({ pushcenter }) => (pushcenter ? "4 / span 8" : "2 / span 8")};
    }

    [dir="rtl"] & {
      grid-column: ${({ pushcenter }) => (pushcenter ? "5 / span 6" : "10 / span 4;")};
    }
  }

  ${getMediaQuery("l")} {
    /* margin-block-end: 8vw; */
    margin-block: ${({ pushcenter }) => (pushcenter ? "auto" : "0 8vw")};
  }

  .${HeadingCN}, .text-block {
    text-align: ${({ pushcenter }) => (pushcenter ? "center" : "start")};
  }

  .${KickerCN}, .${SubtitleCN} {
    ${({ pushcenter }) => (pushcenter ? `${surtitle100}` : `${surtitle50}`)};
  }

  hgroup.${HeadingCN} > h2,
  h2.${HeadingCN} {
    ${({ pushcenter }) => (pushcenter ? `${headline100} font-size: clamp(2.5rem, 0.6250rem + 4.6875vw, 6.25rem);` : `${headline50}`)};
    padding: 0;
    margin-block-end: 0.7rem;
    text-wrap: balance;

    // html.prefers-contrast & {
    //   position: relative;
    //   z-index: 1;
    // }
  }

  hgroup + a {
    margin-block-start: 1.5rem;
  }

  a.focus-visible {
    .dark-theme & {
      outline: 2px solid rgb(var(--pure-white)) !important;
    }
    .light-theme & {
      outline: 2px solid rgb(var(--light-black)) !important;
    }
  }
  html.prefers-contrast & {
    a.focus-visible {
      outline: 2px solid rgb(var(--green)) !important;
    }
  }

  & > a {
    /* text-decoration: none; */
    /* .dark-theme & {
      color: rgb(var(--pure-white));
    }
    .light-theme & {
      color: rgb(var(--light-black));
    }  */
    /* margin-block-start: ${({ pushcenter }) => pushcenter && "1.5rem"}; */
  }

  & > a[target] {
    margin-inline-start: -0.5rem;
  }

  & > a + a {
    display: block !important;
  }

  .text-block {
    margin-block-end: 0.7rem;
    ${body100}

    html.prefers-contrast & {
      position: relative;
      z-index: 1;
    }
  }

  .${ChapoClass} {
    ${light}
  }

  // A11y
  html.prefers-contrast & {
    color: black !important;
    position: relative;

    ::after {
      background: white;
      content: "";
      height: calc(100% + 30px);
      left: 50%;
      position: absolute;
      top: 50%;
      transform: translate(-50%, -50%);
      width: calc(100% + 20px);
      z-index: 0;
    }
  }
`

function generateGradient(colors) {
  return `linear-gradient(${colors.map(color => `rgba(${color.color.split(" ").join(", ")}, ${color.opacity})`).join(", ")})`
}

export default function BlockText({ isPush, heading, chapo, paragraph, link, links, text_width, children, pushCenter, gradient, theme }) {
  const console = useConsole()
  const uid = useId()
  const blockTitleId = `blockTitle-${uid}`
  const learnMoreId = `learnMore-${uid}`

  const textRef = useRef()

  const isTextInView = useInView(textRef, { margin: "100% 0% 0% 0%" })
  const prefersReducedMotion = process.browser && document.documentElement.classList.contains("prefers-reduced-motion")
  const titleGradient = gradient?.colors ? generateGradient(gradient?.colors) : null

  return (
    <Root
      className={`text-block ${textWidths[text_width || "medium"]}`}
      ref={textRef}
      variants={slideUp}
      initial='hidden'
      animate={prefersReducedMotion ? "show" : isTextInView ? "show" : "hide"}
      pushcenter={pushCenter}
      style={{ "--title-gradient": gradient?.colors.length > 0 ? titleGradient : "rgb(var(--green))" }}
    >
      {heading && <Heading isPush={isPush} pushCenter={pushCenter} className={HeadingCN} {...heading} id={blockTitleId} />}
      {chapo && <Chapo className={ChapoClass} text={chapo} />}
      {paragraph && <TextBlock className={ParagraphClass}>{paragraph}</TextBlock>}
      {isPush && (
        <span hidden id={learnMoreId}>
          {link?.label}
        </span>
      )}
      {links?.length > 0 ? (
        links.map(l => (
          <Link key={l.label} {...l} aria-labelledby={isPush ? `${learnMoreId} ${blockTitleId}` : undefined}>
            {l.label}
          </Link>
        ))
      ) : link?.label ? (
        <Link aria-labelledby={isPush ? `${learnMoreId} ${blockTitleId}` : undefined} {...link}>
          {link.label}
        </Link>
      ) : null}
      {/* {link?.label && <Link {...link}>{link.label}</Link>} */}
      {children}
    </Root>
  )
}
